import type { Database } from 'firebase/database';

/** .info is a client-local virtual path: get() sends an invalid server request. */
export function readRtdbConnectionState(
  rtdb: Database,
  database: Pick<typeof import('firebase/database'), 'ref' | 'onValue'>,
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    database.onValue(database.ref(rtdb, '.info/connected'),
      (snapshot) => resolve(snapshot.val() === true), reject, { onlyOnce: true });
  });
}

/**
 * Calls `onReconnect` each time RTDB's connection comes back after a drop (not on the
 * first connect). RTDB holds one socket for the app, so this is the app's signal that
 * the network is usable again, typically a few seconds after waking from sleep.
 */
export function watchRtdbReconnect(onReconnect: () => void): () => void {
  let stopped = false;
  let stop: (() => void) | undefined;
  let wasConnected: boolean | null = null;
  void Promise.all([import('@shared/firebase'), import('firebase/database')]).then(([{ getRtdb }, database]) => {
    if (stopped) return;
    stop = database.onValue(database.ref(getRtdb(), '.info/connected'), (snapshot) => {
      const connected = snapshot.val() === true;
      if (connected && wasConnected === false) onReconnect();
      wasConnected = connected;
    }, () => { /* No connection signal: the timed and wake refreshes still run. */ });
  }).catch(() => {});
  return () => { stopped = true; stop?.(); };
}
